import { useState, useEffect, useCallback, useRef } from 'react';
import { Send, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

export default function AdminMessages() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const endRef = useRef(null);

  const loadThreads = useCallback(() => api.get('/admin/messages').then((r) => setThreads(r.data)).catch(() => {}), []);
  useEffect(() => { loadThreads(); }, [loadThreads]);

  const openThread = async (t) => { setActive(t); const r = await api.get(`/admin/messages/${t.farm_id}`); setMessages(r.data); loadThreads(); };
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    if (!text.trim() || !active) return;
    try { await api.post(`/admin/messages/${active.farm_id}`, { body: text }); setText(''); const r = await api.get(`/admin/messages/${active.farm_id}`); setMessages(r.data); loadThreads(); }
    catch { toast.error('Could not send'); }
  };

  return (
    <div data-testid="admin-messages" className="grid md:grid-cols-3 gap-5 h-[70vh]">
      <div className="bg-white rounded-2xl border border-forest/10 overflow-auto">
        {threads.length === 0 ? <p className="p-8 text-center font-manrope text-dark/50 text-sm">No conversations yet.</p> : threads.map((t) => (
          <button key={t.farm_id} onClick={() => openThread(t)} className={`w-full text-left p-4 border-b border-forest/5 hover:bg-cream/60 ${active?.farm_id === t.farm_id ? 'bg-forest/5' : ''}`} data-testid={`thread-${t.farm_id}`}>
            <div className="flex items-center justify-between"><span className="font-manrope font-semibold text-dark text-sm">{t.farm_name}</span>{t.unread > 0 && <span className="w-5 h-5 bg-gold text-dark text-[10px] font-bold rounded-full flex items-center justify-center">{t.unread}</span>}</div>
            <p className="font-manrope text-xs text-dark/50 truncate mt-0.5">{t.last_message}</p>
          </button>
        ))}
      </div>

      <div className="md:col-span-2 bg-white rounded-2xl border border-forest/10 flex flex-col">
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center text-dark/40"><MessageSquare className="w-12 h-12 mb-3" /><p className="font-manrope">Select a farm to view the conversation</p></div>
        ) : (<>
          <div className="px-5 py-3 border-b border-forest/10"><h3 className="font-playfair font-bold text-dark">{active.farm_name}</h3></div>
          <div className="flex-1 overflow-auto p-5 space-y-3">
            {messages.map((m) => (
              <div key={m.message_id} className={`max-w-[75%] p-3 rounded-2xl font-manrope text-sm ${m.sender_role === 'admin' ? 'ml-auto bg-forest text-cream' : 'bg-cream text-dark'}`}>
                <p>{m.body}</p><p className="text-[10px] opacity-60 mt-1">{m.sender_name} · {new Date(m.created_at).toLocaleString()}</p>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="flex gap-2 p-4 border-t border-forest/10">
            <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()} placeholder="Reply to farm…" className="flex-1 bg-cream border border-forest/20 rounded-xl px-4 py-2.5 font-manrope" data-testid="admin-msg-input" />
            <button onClick={send} className="btn-primary py-2.5 px-5" data-testid="admin-msg-send"><Send className="w-4 h-4" /></button>
          </div>
        </>)}
      </div>
    </div>
  );
}
